import { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Tutor } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { Plus, Edit, Trash2, Search, Mail, Phone, DollarSign, BookOpen, Award, FileDown, Filter, ChevronDown, ShieldOff, ChevronRight, CalendarPlus, Wallet, Receipt } from 'lucide-react'
import { ViewToggle, useStoredView } from '../components/ViewToggle'
import { format } from 'date-fns'
import { ActivityClock } from '../components/ActivityClock'
import { computeOwedByTutor } from '../lib/payout'
import { formatMoney } from '../lib/pricing'
import {
  PayoutSessionRow, OwedPeriod, OWED_PERIOD_LABELS, filterToPeriod, fetchAllPayoutSessions,
} from '../lib/payoutQueries'
import { hasAdminRights } from '../lib/roles'

export function Tutors() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [tutors, setTutors] = useState<Tutor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showColumnFilter, setShowColumnFilter] = useState(false)
  const [view, changeView] = useStoredView('tutorsView')
  const [owedPeriod, setOwedPeriod] = useState<OwedPeriod>(() => {
    const stored = localStorage.getItem('tutorsOwedPeriod')
    return stored && stored in OWED_PERIOD_LABELS ? (stored as OwedPeriod) : 'month'
  })
  const changeOwedPeriod = (next: OwedPeriod) => {
    setOwedPeriod(next)
    localStorage.setItem('tutorsOwedPeriod', next)
  }
  const [owedFrom, setOwedFrom] = useState(() => localStorage.getItem('tutorsOwedFrom') || '')
  const [owedTo, setOwedTo] = useState(() => localStorage.getItem('tutorsOwedTo') || '')
  const changeOwedFrom = (next: string) => {
    setOwedFrom(next)
    localStorage.setItem('tutorsOwedFrom', next)
  }
  const changeOwedTo = (next: string) => {
    setOwedTo(next)
    localStorage.setItem('tutorsOwedTo', next)
  }
  const [payoutSessions, setPayoutSessions] = useState<PayoutSessionRow[]>([])
  const [overrides, setOverrides] = useState<Map<string, number | null>>(new Map())
  const [ratesByTutor, setRatesByTutor] = useState<Map<string, number | null>>(new Map())
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    email: true,
    phone: true,
    level: true,
    hourlyRate: true,
    owed: true,
    subjects: true,
    actions: true
  })
  const [editingTutor, setEditingTutor] = useState<Tutor | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    level: '',
    hourlyRate: '',
    subjects: [] as string[]
  })

  // Check if user is admin
  useEffect(() => {
    if (profile && !hasAdminRights(profile.role)) {
      navigate('/dashboard/students')
    }
  }, [profile, navigate])

  const fetchTutors = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tutors')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Hourly rate is money — stored in the admin-only tutor_billing table.
      const { data: billing } = await supabase.from('tutor_billing').select('tutor_id, hourly_rate')
      const rateMap = new Map((billing || []).map(b => [b.tutor_id, b.hourly_rate]))
      // Null-preserving copy for owed math: null means "no rate set", not $0.
      setRatesByTutor(new Map(rateMap))

      // Owed-to-tutor inputs. Isolated so a failure here never blocks the list.
      try {
        const [{ data: sb }, sessRows] = await Promise.all([
          supabase.from('student_billing').select('student_id, hourly_rate_override'),
          fetchAllPayoutSessions(),
        ])
        setOverrides(new Map((sb || []).map(r => [r.student_id, r.hourly_rate_override])))
        setPayoutSessions(sessRows)
      } catch (payoutError) {
        console.error('Error fetching payout data:', payoutError)
      }

      const formattedTutors = data?.map(tutor => ({
        id: tutor.id,
        name: tutor.name,
        email: tutor.email,
        phone: tutor.phone,
        address: tutor.address || null,
        level: tutor.level,
        hourlyRate: rateMap.get(tutor.id) ?? 0,
        subjects: tutor.subjects,
        active: tutor.active,
        totalMinutesAvailable: 0,
        totalMinutesUsed: 0,
        createdAt: tutor.created_at
      })) || []

      setTutors(formattedTutors)
    } catch (error) {
      console.error('Error fetching tutors:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (hasAdminRights(profile?.role)) {
      fetchTutors()
    }
  }, [profile, fetchTutors])

  // Owed per tutor for the selected period. Same math as the tutor profile
  // page and the invoice (shared src/lib/payout.ts), so views never disagree.
  const owedByTutor = useMemo(
    () => computeOwedByTutor(
      filterToPeriod(payoutSessions, owedPeriod, { from: owedFrom, to: owedTo }),
      overrides,
      ratesByTutor
    ),
    [payoutSessions, overrides, ratesByTutor, owedPeriod, owedFrom, owedTo]
  )

  // "—" when every payable session lacks a rate; a plain $0 otherwise.
  const owedDisplay = (tutorId: string): string => {
    const owed = owedByTutor.get(tutorId)
    if (!owed || owed.payableCount === 0) return formatMoney(0)
    if (owed.missingRateCount === owed.payableCount) return '—'
    return formatMoney(owed.total)
  }

  // Show access denied message for non-admin users
  if (!profile || !hasAdminRights(profile.role)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <ShieldOff className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-500 dark:text-gray-400">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/dashboard/students')}
            className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            Go to Students
          </button>
        </div>
      </div>
    )
  }

  const handleAddTutor = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const { data: newTutor, error } = await supabase
        .from('tutors')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          level: formData.level,
          subjects: formData.subjects,
          active: true
        }])
        .select('id')
        .single()

      if (error) throw error

      // Hourly rate is money — admin-only tutor_billing table.
      if (newTutor) {
        await supabase.from('tutor_billing').insert({
          tutor_id: newTutor.id,
          hourly_rate: parseFloat(formData.hourlyRate) || 0,
        })
      }

      setShowAddModal(false)
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        level: '',
        hourlyRate: '',
        subjects: []
      })
      fetchTutors()
    } catch (error) {
      console.error('Error adding tutor:', error)
    }
  }

  const handleEditTutor = (tutor: Tutor) => {
    setEditingTutor(tutor)
    setFormData({
      name: tutor.name,
      email: tutor.email,
      phone: tutor.phone || '',
      address: tutor.address || '',
      level: tutor.level || '',
      hourlyRate: tutor.hourlyRate.toString(),
      subjects: tutor.subjects
    })
    setShowEditModal(true)
  }

  const handleUpdateTutor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingTutor) return

    try {
      const { error } = await supabase
        .from('tutors')
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          level: formData.level,
          subjects: formData.subjects
        })
        .eq('id', editingTutor.id)

      if (error) throw error

      // Hourly rate is money — upsert into admin-only tutor_billing table.
      await supabase.from('tutor_billing').upsert({
        tutor_id: editingTutor.id,
        hourly_rate: parseFloat(formData.hourlyRate) || 0,
        updated_at: new Date().toISOString(),
      })

      setShowEditModal(false)
      setEditingTutor(null)
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        level: '',
        hourlyRate: '',
        subjects: []
      })
      fetchTutors()
    } catch (error) {
      console.error('Error updating tutor:', error)
    }
  }

  const handleDeleteTutor = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tutor?')) return

    try {
      const { error } = await supabase
        .from('tutors')
        .delete()
        .eq('id', id)

      if (error) throw error
      fetchTutors()
    } catch (error) {
      console.error('Error deleting tutor:', error)
    }
  }

  const exportToCSV = () => {
    const csvData = filteredTutors.map(tutor => {
      const row: any = {}
      if (visibleColumns.name) {
        row.Name = tutor.name
      }
      if (visibleColumns.email) {
        row.Email = tutor.email
      }
      if (visibleColumns.phone) {
        row.Phone = tutor.phone || 'N/A'
      }
      if (visibleColumns.level) {
        row.Level = tutor.level || 'N/A'
      }
      if (visibleColumns.hourlyRate) {
        row['Hourly Rate'] = `$${tutor.hourlyRate || 0}`
      }
      if (visibleColumns.owed) {
        row[`Owed (${OWED_PERIOD_LABELS[owedPeriod]})`] = owedDisplay(tutor.id)
      }
      if (visibleColumns.subjects) {
        row.Subjects = tutor.subjects.join(', ') || 'None'
      }
      return row
    })

    if (csvData.length === 0) {
      alert('No data to export')
      return
    }

    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header =>
          `"${String(row[header as keyof typeof row] || '').replace(/"/g, '""')}"`
        ).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `tutors_${format(new Date(), 'yyyy-MM-dd')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  // Inactive tutors go last; otherwise newest first, as loaded.
  const filteredTutors = tutors.filter(tutor =>
    tutor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tutor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tutor.subjects.some(subject =>
      subject.toLowerCase().includes(searchTerm.toLowerCase())
    )
  ).sort((a, b) => Number(a.active === false) - Number(b.active === false))

  const owedGrandTotal = filteredTutors.reduce((sum, t) => sum + (owedByTutor.get(t.id)?.total ?? 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
      </div>
    )
  }

  return (
    // List view uses the full width so the table's columns fit.
    <div className={view === 'list' ? '' : 'max-w-7xl mx-auto'}>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tutors</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {filteredTutors.length} {filteredTutors.length === 1 ? 'tutor' : 'tutors'} found
          </p>
        </div>
        <div className="flex gap-2">
          <ActivityClock scope="tutors" />
          <ViewToggle view={view} onChange={changeView} />
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <FileDown className="h-5 w-5 mr-2" />
            Export CSV
          </button>
          <div className="relative">
            <button
              onClick={() => setShowColumnFilter(!showColumnFilter)}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Filter className="h-5 w-5 mr-2" />
              Columns
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {showColumnFilter && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-100 rounded-lg shadow-lg z-10 p-3">
                <div className="space-y-2">
                  {Object.entries(visibleColumns).map(([key, value]) => (
                    <label key={key} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })}
                        className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                        {key === 'hourlyRate' ? 'Hourly Rate' : key === 'owed' ? 'Owed' : key === 'actions' ? 'Actions' : key}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors shadow-sm"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Tutor
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, email, or subject..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-surface-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-white"
        />
      </div>

      {/* Owed to tutors (money — admin-only page) */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 px-5 py-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <Wallet className="h-4 w-4 text-brand-500" /> Owed to tutors
            </span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 rounded-lg bg-gray-100 dark:bg-surface-0 p-1 w-fit">
              {(Object.keys(OWED_PERIOD_LABELS) as OwedPeriod[]).map((period) => (
                <button key={period} onClick={() => changeOwedPeriod(period)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    owedPeriod === period
                      ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}>
                  {OWED_PERIOD_LABELS[period]}
                </button>
              ))}
            </div>
          </div>
          {owedPeriod === 'custom' && (
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</span>
                <input type="date" value={owedFrom} onChange={(e) => changeOwedFrom(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm" />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</span>
                <input type="date" value={owedTo} onChange={(e) => changeOwedTo(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm" />
              </label>
              {(owedFrom || owedTo) && (
                <button onClick={() => { changeOwedFrom(''); changeOwedTo('') }}
                  className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-left sm:text-right">
            <p className="text-xl font-bold text-gray-900 dark:text-white leading-none">{formatMoney(owedGrandTotal)}</p>
            <p className="mt-1 text-xs text-gray-400">
              across {filteredTutors.length} {filteredTutors.length === 1 ? 'tutor' : 'tutors'} shown
            </p>
          </div>
          <a
            href={`/invoice/tutor-payouts?period=${owedPeriod}${
              owedPeriod === 'custom' ? `&from=${owedFrom}&to=${owedTo}` : ''
            }`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open a printable payout statement"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-0 transition-colors"
          >
            <Receipt className="h-4 w-4 text-brand-500" />
            Invoice
          </a>
        </div>
      </div>

      {/* Tutors */}
      {filteredTutors.length > 0 ? (
        view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTutors.map((tutor) => (
            <div
              key={tutor.id}
              className="bg-white dark:bg-surface-100 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                    {tutor.name.charAt(0).toUpperCase()}
                  </div>
                  {visibleColumns.name && (
                    <div className="ml-3">
                      <Link
                        to={`/dashboard/tutors/${tutor.id}`}
                        className="group inline-flex items-center gap-1 font-semibold text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                      >
                        {tutor.name}
                        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
                      </Link>
                      <span className="block">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                          tutor.active !== false
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {tutor.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                {visibleColumns.actions && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditTutor(tutor)}
                      title="Quick edit"
                      className="p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTutor(tutor.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                {visibleColumns.email && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{tutor.email}</span>
                  </div>
                )}
                {visibleColumns.phone && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span>{tutor.phone}</span>
                  </div>
                )}
                {visibleColumns.level && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Award className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="capitalize">{tutor.level}</span>
                  </div>
                )}
              </div>

              {/* Rate + owed */}
              {(visibleColumns.hourlyRate || visibleColumns.owed) && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  {visibleColumns.hourlyRate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Hourly Rate</span>
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-lg font-semibold text-gray-900 dark:text-white">{tutor.hourlyRate}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/hr</span>
                      </div>
                    </div>
                  )}
                  {visibleColumns.owed && (
                    <div className={`flex items-center justify-between ${visibleColumns.hourlyRate ? 'mt-2 pt-2 border-t border-gray-200/60 dark:border-gray-700/60' : ''}`}>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Owed · {OWED_PERIOD_LABELS[owedPeriod]}</span>
                      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{owedDisplay(tutor.id)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Subjects */}
              {visibleColumns.subjects && (
                <div>
                  <div className="flex items-center mb-2">
                    <BookOpen className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Subjects</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {tutor.subjects.length > 0 ? (
                      tutor.subjects.map((subject, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded"
                        >
                          {subject}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400 italic">No subjects assigned</span>
                    )}
                  </div>
                </div>
              )}

              {/* Created date */}
              {tutor.createdAt && (
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                  <CalendarPlus className="h-3.5 w-3.5" />
                  Added {format(new Date(tutor.createdAt), 'MMM d, yyyy')}
                </div>
              )}
            </div>
          ))}
        </div>
        ) : (
        /* List view */
        <div className="bg-white dark:bg-surface-100 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-surface-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tutor</th>
                  {(visibleColumns.email || visibleColumns.phone) && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>}
                  {visibleColumns.hourlyRate && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rate</th>}
                  {visibleColumns.owed && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owed · {OWED_PERIOD_LABELS[owedPeriod]}</th>}
                  {visibleColumns.subjects && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Subjects</th>}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Added</th>
                  {visibleColumns.actions && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-surface-100 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTutors.map((tutor) => (
                  <tr key={tutor.id} className="group hover:bg-gray-50 dark:hover:bg-surface-0/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex-shrink-0 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {tutor.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/dashboard/tutors/${tutor.id}`}
                            className="group/link inline-flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                          >
                            {tutor.name}
                            <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/link:text-brand-500 group-hover/link:translate-x-0.5 transition-all" />
                          </Link>
                          <span className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
                              tutor.active !== false
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {tutor.active !== false ? 'Active' : 'Inactive'}
                            </span>
                            {visibleColumns.level && tutor.level && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{tutor.level}</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    {(visibleColumns.email || visibleColumns.phone) && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {visibleColumns.email && (
                          <span className="block max-w-[13rem] truncate text-gray-600 dark:text-gray-300" title={tutor.email}>{tutor.email}</span>
                        )}
                        {visibleColumns.phone && (
                          <span className="block text-xs text-gray-500 dark:text-gray-400">{tutor.phone || '—'}</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.hourlyRate && <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">${tutor.hourlyRate}<span className="text-gray-400 font-normal">/hr</span></td>}
                    {visibleColumns.owed && <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-emerald-600 dark:text-emerald-400">{owedDisplay(tutor.id)}</td>}
                    {visibleColumns.subjects && (
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {tutor.subjects.length > 0 ? tutor.subjects.map((subject, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded">{subject}</span>
                          )) : <span className="text-xs text-gray-400 italic">None</span>}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {tutor.createdAt ? format(new Date(tutor.createdAt), 'MMM d, yyyy') : '—'}
                    </td>
                    {visibleColumns.actions && (
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleEditTutor(tutor)} title="Quick edit" className="p-2 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-500/10 dark:text-gray-400 transition-colors">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteTutor(tutor.id)} title="Delete tutor" className="p-2 rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-500/10 dark:text-gray-400 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )
      ) : (
        <div className="text-center py-12 bg-white dark:bg-surface-100 rounded-lg border border-gray-200 dark:border-gray-700">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">No tutors found</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
            Try adjusting your search criteria or add a new tutor
          </p>
        </div>
      )}

      {/* Add Tutor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Add New Tutor</h2>
            <form onSubmit={handleAddTutor}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Level</label>
                  <select
                    required
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  >
                    <option value="">Select level</option>
                    <option value="Regular">Regular</option>
                    <option value="Premium">Premium</option>
                    <option value="Expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hourly Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subjects (comma separated)</label>
                  <input
                    type="text"
                    placeholder="Math, Science, English"
                    value={formData.subjects.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData,
                      subjects: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700"
                >
                  Add Tutor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tutor Modal */}
      {showEditModal && editingTutor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Edit Tutor</h2>
            <form onSubmit={handleUpdateTutor}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Level</label>
                  <select
                    required
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  >
                    <option value="">Select level</option>
                    <option value="Regular">Regular</option>
                    <option value="Premium">Premium</option>
                    <option value="Expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hourly Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subjects (comma separated)</label>
                  <input
                    type="text"
                    placeholder="Math, Science, English"
                    value={formData.subjects.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData,
                      subjects: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 dark:bg-surface-0 dark:text-white"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingTutor(null)
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      address: '',
                      level: '',
                      hourlyRate: '',
                      subjects: []
                    })
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700"
                >
                  Update Tutor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}